// チンチロリンの画像（決定16）。
//
// 🔴 **素材はゲーム別に持つ**（`public/assets/chinchiro/`）。ポーカーの素材を直に指すと、
//    ⚠️ あちらを差し替えた瞬間にこちらの絵が黙って変わる——コードの import 禁止と同じ理由。
//
// ⚠️ 寸法を**定数で持つ**のは、`<img>` に width/height を書いて**場所を先に取る**ため。
//    書かないと、読み込み終わった瞬間に絵が入って下の要素が飛ぶ。
//
// 💡 **どれも遅延読込**（`loading="lazy"`）。役の絵は6種類あるが、出るのは1局に1枚ずつなので、
//    先に全部落とす意味が無い。

/** `public/` からの相対。⚠️ 先頭スラッシュを付けない（サブパス配信で壊れる）。 */
const DIR = 'assets/chinchiro/images';

/**
 * タイトルロゴ。原画 1536×1024 → `-trim` → 幅480 → **53.1kB**。
 *
 * 焼き直すとき:
 * ```sh
 * magick logo_chinchirorin.png -fuzz 2% -trim +repage -resize 480x \
 *   -strip -quality 86 -define webp:method=6 public/assets/chinchiro/images/logo.webp
 * ```
 *
 * 全画面のタイトルと、ダッシュボードの「メダルであそぶ」のカードの両方で使う。
 * ⚠️ カード側は**遅延読込**にする——「メダルであそぶ」のタブを開くまで読ませない。
 */
export const CHINCHIRO_LOGO = `${DIR}/logo.webp`;
export const CHINCHIRO_LOGO_W = 480;
export const CHINCHIRO_LOGO_H = 289;

/**
 * 勝敗と役の絵は**1枚のシート**（ユーザー提供 `Yaku_chinchirorin.png` 1536×1024）から切り出した。
 *
 * 🔴 **原画は既に背景が透明**だった（四隅の alpha が 0）。⇒ ポーカーの `draw.webp` でやった
 *    「四隅から塗りつぶして抜く」細工は**要らない**。`-trim` だけでよい。
 *    ⚠️ 見た目が「色とりどりの背景に乗っている」ように見えるのは、絵ごとの**光彩**であって
 *    背景ではない。塗りつぶそうとすると、その光彩まで削れる。
 *
 * 焼き直すとき（切り出しの座標は下の表）:
 * ```sh
 * magick Yaku_chinchirorin.png -crop <w>x<h>+<x>+<y> +repage \
 *   -trim +repage -resize <幅>x -strip -quality 86 -define webp:method=6 <名前>.webp
 * ```
 *
 * | 名前 | 切り出し（x, y, w, h） | 焼き上がり |
 * |---|---|---|
 * | win     | 20, 40, 480, 360   | 420×316 |
 * | lose    | 520, 40, 470, 360  | 420×324 |
 * | draw    | 1010, 40, 500, 360 | 420×302 |
 * | pinzoro | 20, 430, 380, 300  | 260×205 |
 * | arashi  | 410, 430, 352, 300 | 260×212 |
 * | shigoro | 790, 430, 370, 300 | 260×204 |
 * | me      | 1170, 430, 350, 300| 260×222 |
 * | menashi | 20, 740, 332, 280  | 260×202 |
 * | hifumi  | 390, 740, 300, 280 | 260×223 |
 * | vs      | 740, 740, 220, 280 | 150×173 |
 * | round   | 1270, 740, 250, 280| 220×227 |
 *
 * ⚠️ **切り出しの箱は隣の絵に届かない大きさにする**。2026-09-20、アラシ・目なし・ヒフミの
 *    3枚で**隣の絵の光彩が切れ端として写り込んでいた**（`-trim` は透明しか削らないので残る）。
 *    ⇒ 焼いたあと**必ず並べて目で見る**こと（下の 💡）。
 *
 * 💡 確かめ方——透明が分かる色の上に並べる:
 * ```sh
 * magick win.webp lose.webp … -background magenta -resize 280x140 \
 *   -gravity center -extent 290x150 -append /tmp/check.png
 * ```
 *
 * ⚠️ シートには **`DRAW` の英字版**も入っているが**焼いていない**。
 *    引き分けには日本語の `draw`（「引き分け」）を使う——勝ち・負けと**同じ並びの絵柄**で、
 *    2枚持つと「どちらが正か」が分からなくなるため。要るときは上の手順で足せる。
 */
export const CHINCHIRO_WIN = `${DIR}/win.webp`;
export const CHINCHIRO_WIN_W = 420;
export const CHINCHIRO_WIN_H = 316;
export const CHINCHIRO_LOSE = `${DIR}/lose.webp`;
export const CHINCHIRO_LOSE_W = 420;
export const CHINCHIRO_LOSE_H = 324;
export const CHINCHIRO_DRAW = `${DIR}/draw.webp`;
export const CHINCHIRO_DRAW_W = 420;
export const CHINCHIRO_DRAW_H = 302;

/** 「VS」。ラウンドが始まるときの合図。 */
export const CHINCHIRO_VS = `${DIR}/vs.webp`;
export const CHINCHIRO_VS_W = 150;
export const CHINCHIRO_VS_H = 173;
// ⚠️ 「ROUND」の絵（round.webp）は **2026-09-21 に画面から外し、ファイルごと消した**
//    （実機検証2回目「不要」）。戻すときは git 履歴から取り出すこと。

/** 勝敗の絵（3種）。⚠️ 並びは `Outcome` と揃える。 */
export const OUTCOME_ART = {
  win: { src: CHINCHIRO_WIN, w: CHINCHIRO_WIN_W, h: CHINCHIRO_WIN_H },
  lose: { src: CHINCHIRO_LOSE, w: CHINCHIRO_LOSE_W, h: CHINCHIRO_LOSE_H },
  draw: { src: CHINCHIRO_DRAW, w: CHINCHIRO_DRAW_W, h: CHINCHIRO_DRAW_H },
} as const;

/**
 * 役の絵（6種）。
 *
 * ⚠️ **アラシと○の目は数字を持たない**——絵は1枚きりで、「6のアラシ」の 6 は
 *    絵の下に文字で添える。数字ぶんの絵を作ると 2〜6 と 1〜6 で11枚になる。
 */
export const YAKU_ART = {
  pinzoro: `${DIR}/pinzoro.webp`,
  arashi: `${DIR}/arashi.webp`,
  shigoro: `${DIR}/shigoro.webp`,
  me: `${DIR}/me.webp`,
  menashi: `${DIR}/menashi.webp`,
  hifumi: `${DIR}/hifumi.webp`,
} as const;

/** 役の絵の寸法。⚠️ 幅は揃えてあるが**高さは絵ごとに違う**（場所取りに要る）。 */
export const YAKU_ART_SIZE = {
  pinzoro: { w: 260, h: 205 },
  arashi: { w: 260, h: 212 },
  shigoro: { w: 260, h: 204 },
  me: { w: 260, h: 222 },
  menashi: { w: 260, h: 202 },
  hifumi: { w: 260, h: 223 },
} as const;
